/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { space, text } from "@octopusdeploy/design-system-tokens";
import { TriggerActionType, TriggerFilterType, toDeploymentActionPackage, triggerActionTypeIs, triggerFilterTypeIs, triggerIdIs } from "@octopusdeploy/octopus-server-client";
import type { DeploymentProcessResource, DeploymentActionPackageResource, TriggerFeedFilterResource, TriggerResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { DeploymentActionChip } from "~/components/Chips";
import InternalLink from "~/components/Navigation/InternalLink";
interface TriggerNameAndLinkProps {
    trigger: TriggerResource;
    projectSlug: string;
}
export function TriggerNameAndLink({ trigger, projectSlug }: TriggerNameAndLinkProps) {
    return (<div className={styles.nameWrapper}>
            <InternalLink to={getTriggerRedirectUrl(trigger, projectSlug)}>
                <span className={styles.triggerName}>{trigger.Name}</span>
            </InternalLink>
        </div>);
}
interface FeedTriggerDescriptionProps {
    trigger: TriggerResource;
    builtInFeedPackageActions: DeploymentActionPackageResource[];
    projectSlug: string;
    deploymentProcess: DeploymentProcessResource;
    project?: ProjectResource;
}
export function FeedTriggerDescription({ trigger, builtInFeedPackageActions, projectSlug, deploymentProcess, project }: FeedTriggerDescriptionProps) {
    const packages = (trigger.Filter as TriggerFeedFilterResource).Packages;
    if (packages.length === 0)
        return <>No packages monitored</>;
    if (trigger.Id === "built-in-feed" && builtInFeedPackageActions?.length > 0) {
        let builtInFeedPackage = builtInFeedPackageActions[0];
        if (project?.ReleaseCreationStrategy?.ReleaseCreationPackage) {
            const releaseCreationPackage = project.ReleaseCreationStrategy.ReleaseCreationPackage;
            const matchingPackage = builtInFeedPackageActions.find((a) => {
                const packageReferenceIndex = a.PackageReference ?? "";
                const releaseCreationPackageReference = releaseCreationPackage.PackageReference ?? "";
                return a.DeploymentAction === releaseCreationPackage.DeploymentAction && packageReferenceIndex === releaseCreationPackageReference;
            });
            if (matchingPackage) {
                builtInFeedPackage = matchingPackage;
            }
        }
        return (<span className={styles.description}>
                Create a release when the package {builtInFeedPackage.PackageReference} is pushed to the built-in feed connected to <DeploymentActionChip stepName={builtInFeedPackage.DeploymentAction}/>
            </span>);
    }
    const pkg = toDeploymentActionPackage(packages[0], deploymentProcess);
    if (packages.length > 1)
        return (<span className={styles.description}>
                Create a release when a package is pushed to the external feeds connected to <DeploymentActionChip stepName={pkg.DeploymentAction}/> <InternalLink to={getTriggerRedirectUrl(trigger, projectSlug)}>Show&nbsp;more</InternalLink>
            </span>);
    return (<span className={styles.description}>
            Create a release when the {packageName(pkg)} is pushed to the external feed connected to <DeploymentActionChip stepName={pkg.DeploymentAction}/>
        </span>);
}
const packageName = (pkg: DeploymentActionPackageResource) => (pkg.PackageReference ? "package " + pkg.PackageReference : "primary package");
export const getTriggerRedirectUrl = (trigger: TriggerResource, projectSlug: string) => {
    const urlParams = { spaceId: trigger.SpaceId, projectSlug: projectSlug, triggerId: trigger.Id };
    switch (true) {
        case triggerIdIs(trigger, "built-in-feed"):
            return links.builtInFeedTriggerPage.generateUrl({ spaceId: trigger.SpaceId, projectSlug: projectSlug });
        case triggerActionTypeIs(trigger, TriggerActionType.CreateRelease) && triggerFilterTypeIs(trigger, TriggerFilterType.FeedFilter):
            return links.releaseCreationFeedTriggerPage.generateUrl(urlParams);
        case triggerActionTypeIs(trigger, TriggerActionType.CreateRelease) && triggerFilterTypeIs(trigger, TriggerFilterType.GitFilter):
            return links.releaseCreationGitTriggerPage.generateUrl(urlParams);
        case triggerActionTypeIs(trigger, TriggerActionType.RunRunbook):
            return links.runbookScheduledTriggerPage.generateUrl(urlParams);
        case triggerActionTypeIs(trigger, TriggerActionType.AutoDeploy):
            return links.deploymentTargetTriggerPage.generateUrl(urlParams);
        default:
            return links.deploymentScheduledTriggerPage.generateUrl(urlParams);
    }
};
const styles = {
    triggerName: css({ font: text.body.bold.large }),
    nameWrapper: css({
        "@media (min-width: 900px)": {
            minWidth: 200,
        },
        "@media (min-width: 1536px)": {
            minWidth: 300,
        },
    }),
    description: css({ lineHeight: space[24] }),
};
